import createTransport from 'api/transport/createTransport';
import factory from 'state/factory';
import trimAmpWebHost from 'utils/trimAmpWebHost';
import { AxiosResponse } from 'axios';
import { concatTitleAndVersion } from 'utils/trackFormatter';
import { getAlbumUrl, getArtistUrl, getLiveUrl, getTrackUrl } from 'utils/url';
import { getPodcastUrl } from 'state/Podcast/helpers';
import { resultType } from 'state/SearchNew/types';
import { SEARCH_FILTERS } from 'state/SearchNew/constants';
import { newSearchRequest as searchRequest } from 'state/SearchNew/services';
import { slugify } from 'utils/string';
import { STATION_TYPE } from 'constants/stationTypes';
import { THUMB_RES } from 'components/MediaServerImage';

import type { Result, SearchResponse } from 'state/SearchNew/types';

const transport = createTransport(factory);

class SearchResponseError extends Error {
  response?: AxiosResponse;
}

const getKeywordCatalogType = (webUrl: string) => {
  if (webUrl.includes('albums')) {
    return STATION_TYPE.ALBUM;
  }
  if (webUrl.includes('playlist')) {
    return STATION_TYPE.COLLECTION;
  }
  if (webUrl.includes('songs')) {
    return STATION_TYPE.TRACK;
  }
  if (webUrl.includes('artist')) {
    return STATION_TYPE.ARTIST;
  }
  if (webUrl.includes('live')) {
    return STATION_TYPE.LIVE;
  }
  if (webUrl.includes('podcast')) {
    return STATION_TYPE.PODCAST;
  }
  return null;
};

const _transform = (result: Result) => {
  switch (result.typeName) {
    case resultType.ARTIST:
      return {
        catalogId: result.id,
        catalogType: STATION_TYPE.ARTIST,
        description: 'Artist',
        id: result.id,
        imgUrl: result.image || result.imageUrl,
        imgWidth: THUMB_RES,
        title: result.name,
        url: getArtistUrl(result.id, result.name ?? ''),
      };
    case resultType.ALBUM:
      return {
        artistId: result.artistId,
        artistName: result.artistName,
        catalogId: result.id,
        catalogType: STATION_TYPE.ALBUM,
        description: result.artistName,
        descriptionUrl: result.artistId
          ? getArtistUrl(result.artistId, result.artistName ?? '')
          : null,
        explicitLyrics: result.explicitLyrics,
        id: result.id,
        imgUrl: result.image || result.imageUrl,
        imgWidth: THUMB_RES,
        isOnDemandTrack: result.playbackRights?.onDemand,
        title: result.title,
        url: result.artistId
          ? getAlbumUrl(result.artistId, result.artistName ?? '', result.id, result.title ?? '')
          : null,
      };
    case resultType.TRACK:
      return {
        artistId: result.artistId,
        artistName: result.artistName,
        catalogId: result.id,
        catalogType: STATION_TYPE.TRACK,
        description: `${result.artistName} • ${result.albumName}`,
        descriptionUrl: result.artistId
          ? getArtistUrl(result.artistId, result.artistName ?? '')
          : null,
        explicitLyrics: result.explicitLyrics,
        id: result.id,
        imgUrl: result.image || result.imageUrl,
        imgWidth: THUMB_RES,
        isOnDemandTrack: result.playbackRights?.onDemand,
        title: concatTitleAndVersion(result.title, result.version),
        url: result.artistId
          ? getTrackUrl(result.artistId, result.artistName ?? '', result.id, result.title ?? '')
          : null,
      };
    case resultType.STATION:
      return {
        callLetters: result.callLetters,
        catalogId: result.id,
        catalogType: STATION_TYPE.LIVE,
        description: result.description,
        id: result.id,
        imgUrl: result.image || result.imageUrl || getLiveUrl(result.id, result.name ?? ''),
        imgWidth: THUMB_RES,
        title: result.name,
        url: getLiveUrl(result.id, result.name ?? ''),
      };
    case resultType.PODCAST:
      return {
        catalogId: result.id,
        catalogType: STATION_TYPE.PODCAST,
        description: result.description,
        id: result.id,
        imgUrl: result.image || result.imageUrl,
        imgWidth: THUMB_RES,
        title: result.title,
        url: getPodcastUrl(result.id, slugify(result.title ?? '')),
      };
    case resultType.PLAYLIST:
      return {
        author: result.author,
        catalogId: result.id,
        catalogType: STATION_TYPE.COLLECTION,
        catalogUserId: result.userId,
        description: result.description,
        id: result.id,
        imgUrl: result.urls?.image ?? (result.image || result.imageUrl),
        imgWidth: THUMB_RES,
        isOnDemandTrack: true,
        title: result.name ?? result.title,
        url: trimAmpWebHost(result.urls?.web ?? ''),
      };
    case resultType.KEYWORDS:
      return {
        id: result.id,
        catalogId: result.contentId,
        catalogType: getKeywordCatalogType(result.webUrl ?? ''),
        description: result.description,
        imgUrl: result.image || result.imageUrl,
        imgWidth: THUMB_RES,
        title: result.name ?? result.title,
        contentType: result.contentType,
        url: trimAmpWebHost(result.webUrl ?? ''),
      };
    default:
      return null;
  }
};

export const _process = (data: SearchResponse) => {
  const processedData: { bestMatch?: any; results?: Array<any>; nextPage?: string } = {};
  if (data.bestMatch) {
    processedData.bestMatch = _transform(data.bestMatch);
  }

  if (data.results) {
    processedData.results = data.results.map(result => _transform(result));
  }

  if (data.nextPage) {
    processedData.nextPage = data.nextPage;
  }

  return processedData;
};

export const search = function search(
  kw: string,
  filterType: string,
  opts: any,
  profileId: string | number,
  sessionId: string,
  ampUrl: string,
  host: string,
  marketId: string | number,
  searchVariant?: string,
  nextPageKey?: string
): Promise<SearchResponse> {
  const resultFilters = Object.keys(SEARCH_FILTERS)
    .filter(type => type !== 'ALL')
    .reduce(
      (memo, key) => ({
        ...memo,
        [SEARCH_FILTERS[key]]: filterType ? filterType === SEARCH_FILTERS[key] : true,
      }),
      {}
    );

  const requestParams = {
    boostMarketId: marketId,
    bundle: filterType === SEARCH_FILTERS.ALL || filterType === SEARCH_FILTERS.ALBUM,
    keyword: true,
    keywords: kw,
    ...opts,
    ...resultFilters,
  };

  if (nextPageKey) {
    requestParams.pageKey = nextPageKey;
  }

  return transport(
    searchRequest({ ampUrl, host, params: requestParams, profileId, sessionId, searchVariant })
  ).then((response: AxiosResponse<SearchResponse>) => {
    const { status, statusText, data } = response;

    if (status >= 200 && status < 300) {
      return data;
    }

    const error = new SearchResponseError(statusText);
    error.response = response;
    throw error;
  });
};
